import { ChevronRight } from "lucide-react";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  formatPersistentEditMetadata,
  formatPersistentEditValue,
  type OverrideKind,
} from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import {
  AI_SPREADSHEET_OVERRIDE_REVIEW_REASON,
  type IAiSpreadsheetCellLocation,
  type IAiSpreadsheetCellOverride,
} from "@/payroll/ai-script/types";
import { OverrideRowActions } from "./override-row-actions";
import {
  BoldValue,
  CellLocationLink,
  EditCard,
  EditValueChange,
} from "./shared";

interface OverrideRowProps {
  override: IAiSpreadsheetCellOverride;
  businessId: string | null;
  runId: string;
  kind: OverrideKind;
  canResolve: boolean | null;
  onFocus?: () => void;
  onGoToCell: (cell: IAiSpreadsheetCellLocation) => void;
  onResolved?: () => void;
  // Fired on a successful discard/accept (which advance the server workbook
  // version) so the session undo/redo stack can be reset.
  onWorkbookMutated?: () => void;
}

export const OverrideRow = ({
  override,
  businessId,
  runId,
  kind,
  canResolve,
  onFocus,
  onGoToCell,
  onResolved,
  onWorkbookMutated,
}: OverrideRowProps) => {
  const isAmbiguous =
    override.review_reason === AI_SPREADSHEET_OVERRIDE_REVIEW_REASON.ambiguous;
  // In the list the cards sit under a heading that names their status, so only
  // the ones that break the pattern need to repeat it.
  const isListCard = !!onFocus;

  const statusBadge =
    kind === "dropped" ? (
      <Badge variant="gray" size="sm" className="shrink-0">
        Dropped
      </Badge>
    ) : kind === "needs_review" ? (
      <Badge variant="gold" size="sm" className="shrink-0">
        Needs review
      </Badge>
    ) : !isListCard ? (
      <Badge variant="green" size="sm" className="shrink-0">
        Applied
      </Badge>
    ) : null;

  const openEditButton = isListCard ? (
    <Button
      type="button"
      variant="text"
      size="iconSm"
      aria-label="Open edit history"
      onClick={(event) => {
        event.stopPropagation();
        onFocus?.();
      }}
    >
      <ChevronRight className="text-tertiary-text" />
    </Button>
  ) : null;

  const note =
    kind === "dropped" ? (
      "This row wasn't on the latest run, so the edit couldn't re-apply."
    ) : isAmbiguous ? (
      "This edit matched more than one row — pick the row it belongs to."
    ) : kind === "needs_review" ? (
      <>
        The payroll logic now generates{" "}
        <BoldValue>
          {formatPersistentEditValue(override.generated_value)}
        </BoldValue>{" "}
        for this cell.
      </>
    ) : null;

  const metadata = formatPersistentEditMetadata(override);

  const actions = (
    <OverrideRowActions
      override={override}
      businessId={businessId}
      runId={runId}
      kind={kind}
      canResolve={canResolve}
      isListCard={isListCard}
      onFocus={onFocus}
      onResolved={onResolved}
      onWorkbookMutated={onWorkbookMutated}
    />
  );

  const body = (
    <>
      <Stack
        direction="row"
        gap="sm"
        align="center"
        justify="between"
        fullWidth
        className="min-w-0"
      >
        {override.cell ? (
          <CellLocationLink cell={override.cell} onGoToCell={onGoToCell} />
        ) : (
          <Typography variant="subtitleW3" textColor="tertiary">
            {isAmbiguous ? "Matched multiple rows" : "Row not on this run"}
          </Typography>
        )}
        <Stack direction="row" gap="xs" align="center">
          {statusBadge}
          {openEditButton}
        </Stack>
      </Stack>

      <EditValueChange
        from={override.previous_value}
        to={override.value?.computed_value}
      />

      {note ? (
        <Typography variant="bodyW2" textColor="secondary">
          {note}
        </Typography>
      ) : null}

      <Typography variant="captionW1" textColor="tertiary" className="truncate">
        {metadata}
      </Typography>
    </>
  );

  const content = (
    <>
      {body}
      {actions}
      {canResolve === false ? (
        <Typography variant="captionW1" textColor="tertiary">
          You have view-only access to this edit.
        </Typography>
      ) : null}
    </>
  );

  // Opened on its own, the card is already the page — no shell, no click target.
  if (!isListCard) {
    return (
      <Stack gap="sm" fullWidth align="stretch" className="min-w-0">
        {content}
      </Stack>
    );
  }

  return (
    <EditCard onClick={onFocus} data-persistent-edit-card>
      {content}
    </EditCard>
  );
};
